import React, { useState, useEffect } from 'react';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, pdf, Image,BlobProvider } from '@react-pdf/renderer';
import logo from '../images/logo.jpeg';
// PDF Styles (reuse from invoice)
const pdfStyles = StyleSheet.create({
  page: {
    padding: 10,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: '#0891b2',
    paddingBottom: 20,
  },
  companyName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0891b2',
  },
  companyDetails: {
    fontSize: 10,
    color: '#666',
    marginTop: 5,
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0891b2',
    textAlign: 'right',
  },
  subtitle: {
    fontSize: 10,
    color: '#666',
    textAlign: 'right',
    marginTop: 5,
  },
  infoSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
    backgroundColor: '#f8f9fa',
    padding: 15,
    borderRadius: 5,
  },
  infoBlock: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 10,
    color: '#666',
    marginBottom: 3,
    textTransform: 'uppercase',
  },
  infoValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#333',
  },
  customerSection: {
    marginBottom: 30,
    padding: 15,
    backgroundColor: '#f8f9fa',
    borderRadius: 5,
  },
  table: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#0891b2',
    color: 'white',
    padding: 10,
    fontWeight: 'bold',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    padding: 8,
  },
  tableRowAlt: {
    backgroundColor: '#f9f9f9',
  },
  col1: { width: '35%', paddingHorizontal: 5 },
  col2: { width: '10%', textAlign: 'center', paddingHorizontal: 5 },
  col3: { width: '15%', textAlign: 'right', paddingHorizontal: 5 },
  col4: { width: '15%', textAlign: 'right', paddingHorizontal: 5 },
  col5: { width: '10%', textAlign: 'center', paddingHorizontal: 5 },
  col6: { width: '15%', textAlign: 'right', paddingHorizontal: 5 },
  totals: {
    marginTop: 20,
    alignItems: 'flex-end',
  },
  totalRow: {
    flexDirection: 'row',
    padding: 5,
    width: '50%',
  },
  totalLabel: {
    flex: 1,
    textAlign: 'right',
    marginRight: 10,
    fontSize: 11,
  },
  totalValue: {
    width: 120,
    textAlign: 'right',
    fontSize: 11,
  },
  grandTotal: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0891b2',
    borderTopWidth: 2,
    borderTopColor: '#0891b2',
    paddingTop: 10,
    marginTop: 5,
  },
  notes: {
    marginTop: 30,
    padding: 15,
    backgroundColor: '#f8f9fa',
    borderRadius: 5,
  },
  notesTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#0891b2',
  },
  notesText: {
    fontSize: 10,
    lineHeight: 1.4,
    color: '#666',
  },
  bankDetails: {
    marginTop: 15,
    padding: 15,
    backgroundColor: '#fff8e1',
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#ffb300',
  },
  bankTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#f57c00',
  },
  bankText: {
    fontSize: 10,
    marginBottom: 3,
    color: '#666',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    color: '#666',
    fontSize: 9,
    borderTopWidth: 1,
    borderTopColor: '#ddd',
    paddingTop: 10,
  },
  compliance: {
    fontSize: 8,
    color: '#0891b2',
    marginTop: 5,
  },
  logo: { 
    width: 30,  
    height: 30,
    objectFit: 'contain',
  },
});

// PDF Document Component for Purchase Order
const PurchaseOrderPDFDocument = ({ purchaseOrderData }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: purchaseOrderData.currency || 'AED',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <View>
             <Image src={logo} style={pdfStyles.logo}/>
            <Text style={pdfStyles.companyName}>{purchaseOrderData.company.name}</Text>
            <Text style={pdfStyles.companyDetails}>{purchaseOrderData.company.address}</Text>
            <Text style={pdfStyles.companyDetails}>TRN: {purchaseOrderData.company.trn}</Text>
          </View>
          <View>
            <Text style={pdfStyles.invoiceTitle}>PURCHASE ORDER</Text>
            <Text style={pdfStyles.subtitle}>UAE Federal Tax Authority</Text>
          </View>
        </View>

        {/* PO Info */}
        <View style={pdfStyles.infoSection}>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>PO Number</Text>
            <Text style={pdfStyles.infoValue}>{purchaseOrderData.poNumber}</Text>
          </View>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>PO Date</Text>
            <Text style={pdfStyles.infoValue}>{formatDate(purchaseOrderData.poDate)}</Text>
          </View>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Required Date</Text>
            <Text style={pdfStyles.infoValue}>{formatDate(purchaseOrderData.requiredDate)}</Text>
          </View>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Currency</Text>
            <Text style={pdfStyles.infoValue}>{purchaseOrderData.currency}</Text>
          </View>
        </View>

        {/* Supplier Details */}
        <View style={pdfStyles.customerSection}>
          <Text style={pdfStyles.infoLabel}>Supplier:</Text>
          <Text style={[pdfStyles.infoValue, { marginTop: 5, marginBottom: 3 }]}>
            {purchaseOrderData.supplierName}
          </Text>
          <Text style={pdfStyles.companyDetails}>{purchaseOrderData.supplierAddress}</Text>
          {purchaseOrderData.supplierTrn && (
            <Text style={pdfStyles.companyDetails}>TRN: {purchaseOrderData.supplierTrn}</Text>
          )}
          <Text style={pdfStyles.companyDetails}>{purchaseOrderData.supplierCity}, {purchaseOrderData.supplierCountry}</Text>
          {purchaseOrderData.supplierEmail && (
            <Text style={pdfStyles.companyDetails}>Email: {purchaseOrderData.supplierEmail}</Text>
          )}
        </View>

        {/* Items Table */}
        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableHeader}>
            <Text style={pdfStyles.col1}>Description</Text>
            <Text style={pdfStyles.col2}>Qty</Text>
            <Text style={pdfStyles.col3}>Unit Price</Text>
            <Text style={pdfStyles.col4}>Discount</Text>
            <Text style={pdfStyles.col5}>VAT</Text>
            <Text style={pdfStyles.col6}>Total</Text>
          </View>

          {purchaseOrderData.items.map((item, index) => {
            const subtotal = item.quantity * item.unitPrice;
            const discountAmount = subtotal * (item.discountPercent / 100);
            const afterDiscount = subtotal - discountAmount;
            const vatAmount = afterDiscount * (item.vatPercent / 100);
            const total = afterDiscount + vatAmount;

            return (
              <View key={index} style={[pdfStyles.tableRow, index % 2 === 1 ? pdfStyles.tableRowAlt : {}]}>
                <Text style={pdfStyles.col1}>{item.description}</Text>
                <Text style={pdfStyles.col2}>{item.quantity} {item.unit}</Text>
                <Text style={pdfStyles.col3}>{formatCurrency(item.unitPrice)}</Text>
                <Text style={pdfStyles.col4}>{item.discountPercent}%</Text>
                <Text style={pdfStyles.col5}>{item.vatPercent}%</Text>
                <Text style={pdfStyles.col6}>{formatCurrency(total)}</Text>
              </View>
            );
          })}
        </View>

        {/* Totals Section */}
        <View style={pdfStyles.totals}>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Subtotal:</Text>
            <Text style={pdfStyles.totalValue}>{formatCurrency(purchaseOrderData.totals.subtotal)}</Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Total Discount:</Text>
            <Text style={pdfStyles.totalValue}>{formatCurrency(purchaseOrderData.totals.totalDiscount)}</Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Taxable Amount:</Text>
            <Text style={pdfStyles.totalValue}>{formatCurrency(purchaseOrderData.totals.taxableAmount)}</Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>VAT Amount (5%):</Text>
            <Text style={pdfStyles.totalValue}>{formatCurrency(purchaseOrderData.totals.vatTotal)}</Text>
          </View>
          <View style={[pdfStyles.totalRow, pdfStyles.grandTotal]}>
            <Text style={pdfStyles.totalLabel}>Total Amount:</Text>
            <Text style={pdfStyles.totalValue}>{formatCurrency(purchaseOrderData.totals.totalDue)}</Text>
          </View>
        </View>

        {/* Notes */}
        {purchaseOrderData.notes && (
          <View style={pdfStyles.notes}>
            <Text style={pdfStyles.notesTitle}>Notes / Terms:</Text>
            <Text style={pdfStyles.notesText}>{purchaseOrderData.notes}</Text>
          </View>
        )}

        {/* Bank Details */}
        <View style={pdfStyles.bankDetails}>
          <Text style={pdfStyles.bankTitle}>Payment Details:</Text>
          <Text style={pdfStyles.bankText}>Bank: {purchaseOrderData.bankDetails.bankName}</Text>
          <Text style={pdfStyles.bankText}>IBAN: {purchaseOrderData.bankDetails.iban}</Text>
          <Text style={pdfStyles.bankText}>Swift: {purchaseOrderData.bankDetails.swift}</Text>
        </View>

        {/* Footer */}
        <View style={pdfStyles.footer}>
          <Text>This is a computer generated purchase order. No signature required.</Text>
          <Text style={pdfStyles.compliance}>
            🇦🇪 UAE VAT Compliant | Federal Tax Authority Approved | TRN: {purchaseOrderData.company.trn}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const PurchaseOrderEntry = ({ onSubmit = () => {}, initialData, suppliers = [], companyInfo = null }) => {
  const [formData, setFormData] = useState(initialData || {
    poNumber: 'PO-202603-001',
    poDate: '2026-03-11',
    requiredDate: '2026-04-10',
    paymentTerms: 'Net 30',
    currency: 'AED',

    // Supplier Information
    supplierName: 'Tech Supplies LLC',
    supplierTrn: '',
    supplierEmail: 'orders@techsupplies.ae',
    supplierAddress: 'Industrial Area, Dubai',
    supplierCity: 'Dubai',
    supplierCountry: 'UAE',

    // Line Items
    items: [
      {
        description: 'Office Supplies',
        quantity: 10,
        unit: 'Units',
        unitPrice: 150,
        discountPercent: 0,
        vatPercent: 5,
        total: 1575
      }
    ],

    // Notes
    notes: 'Please deliver to our main office. Contact person: John Doe - 050-1234567',

    // Bank Details
    bankDetails: {
      iban: 'AE07 0331 2345 6789 0123 456',
      bankName: 'Emirates NBD',
      swift: 'EBILAEA'
    }
  });

  const [company] = useState(companyInfo || {
    name: 'Diamond Quality Constructions',
    trn: '104836477000001',
    address: 'Sharja, UAE',
    logo: '/logo.jpeg'
  });

  const [items, setItems] = useState(formData.items);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [pdfData, setPdfData] = useState(null);
  const [shouldDownload, setShouldDownload] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadSession, setUploadSession] = useState({
    isActive: false,
    files: [],
    totalSize: 0
  });

  // UAE Emirates list
  const emirates = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'];

  // Calculate line item total
  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unitPrice;
    const discountAmount = subtotal * (item.discountPercent / 100);
    const afterDiscount = subtotal - discountAmount;
    const vatAmount = afterDiscount * (item.vatPercent / 100);
    return afterDiscount + vatAmount;
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    let taxableAmount = 0;
    let vatTotal = 0;

    items.forEach(item => {
      const itemSubtotal = item.quantity * item.unitPrice;
      const itemDiscount = itemSubtotal * (item.discountPercent / 100);
      const itemAfterDiscount = itemSubtotal - itemDiscount;
      const itemVat = itemAfterDiscount * (item.vatPercent / 100);

      subtotal += itemSubtotal;
      totalDiscount += itemDiscount;
      taxableAmount += itemAfterDiscount;
      vatTotal += itemVat;
    });

    const totalDue = taxableAmount + vatTotal;

    return {
      subtotal,
      totalDiscount,
      taxableAmount,
      vatTotal,
      totalDue
    };
  };

  const { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue } = calculateTotals();

  // Handle supplier selection
  const handleSupplierSelect = (supplierId) => {
    const supplier = suppliers.find(s => s.id === parseInt(supplierId));
    if (supplier) {
      setFormData({
        ...formData,
        supplierName: supplier.name,
        supplierTrn: supplier.trn || '',
        supplierEmail: supplier.email || '',
        supplierAddress: supplier.address || '',
        supplierCity: supplier.city || '',
        supplierCountry: supplier.country || 'UAE'
      });
    }
  };

  // Add new item row
  const addItem = () => {
    setItems([...items, {
      description: '',
      quantity: 1,
      unit: 'Unit',
      unitPrice: 0,
      discountPercent: 0,
      vatPercent: 5,
      total: 0
    }]);
  };

  // Remove item row
  const removeItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    }
  };

  // Update item field
  const updateItem = (index, field, value) => {
    const newItems = [...items];

    if (['quantity', 'unitPrice', 'discountPercent', 'vatPercent'].includes(field)) {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }

    newItems[index].total = calculateItemTotal(newItems[index]);

    setItems(newItems);
  };

  // Update form field
  const updateField = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // Update bank details
  const updateBankDetails = (field, value) => {
    setFormData({
      ...formData,
      bankDetails: {
        ...formData.bankDetails,
        [field]: value
      }
    });
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      uploadedAt: new Date()
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
    setUploadSession(prev => ({
      ...prev,
      files: [...prev.files, ...newFiles],
      totalSize: prev.totalSize + newFiles.reduce((sum, f) => sum + f.size, 0)
    }));
  };

  // Remove uploaded file
  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    setUploadSession(prev => ({
      ...prev,
      files: prev.files.filter(f => f.id !== fileId),
      totalSize: prev.totalSize - prev.files.find(f => f.id === fileId)?.size || 0
    }));
  };

  // Start upload session
  const startUploadSession = () => {
    setUploadSession(prev => ({ ...prev, isActive: true }));
  };

  // End upload session
  const endUploadSession = () => {
    setUploadSession({
      isActive: false,
      files: [],
      totalSize: 0
    });
    setUploadedFiles([]);
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '/');
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.supplierName) newErrors.supplierName = 'Supplier name is required';
    if (!formData.poNumber) newErrors.poNumber = 'PO number is required';

    items.forEach((item, index) => {
      if (!item.description) {
        newErrors[`item_${index}_description`] = 'Description required';
      }
      if (item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const purchaseOrderData = {
        ...formData,
        items: items,
        company: company,
        totals: { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue },
        uploadedFiles: uploadedFiles,
        formattedDates: {
          poDate: formatDateForDisplay(formData.poDate),
          requiredDate: formatDateForDisplay(formData.requiredDate)
        }
      };

      // Prepare PDF data
      const pdfPurchaseOrderData = {
        ...formData,
        items: items,
        company: company,
        totals: { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue }
      };

      // Set PDF data and trigger download
      setPdfData(pdfPurchaseOrderData);
      setShouldDownload(true);

      // If onSubmit is provided and not the default empty function, use it
      if (onSubmit && onSubmit !== (() => {})) {
        onSubmit(purchaseOrderData);
      } else {
        // Default behavior: show success message
        setTimeout(() => {
          alert('Purchase Order saved successfully! PDF will be downloaded automatically.');
        }, 100);
      }
    }
  };

  // Effect to handle PDF download
  useEffect(() => {
    if (shouldDownload && pdfData) {
      // Generate and download PDF
      const generateAndDownloadPDF = async () => {
        try {
          const pdfDocument = <PurchaseOrderPDFDocument purchaseOrderData={pdfData} />;
          const blob = await pdf(pdfDocument).toBlob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `PurchaseOrder-${pdfData.poNumber || 'New'}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          // Reset the trigger
          setShouldDownload(false);
          setPdfData(null);
        } catch (error) {
          console.error('Error generating PDF:', error);
          alert('Error generating PDF. Please try again.');
          setShouldDownload(false);
          setPdfData(null);
        }
      };

      generateAndDownloadPDF();
    }
  }, [shouldDownload, pdfData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with animated gradient */}
        <div className="bg-gradient-to-r from-primary via-secondary to-accent rounded-t-2xl p-4 sm:p-8 text-white shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">Purchase Order Entry</h1>
              <p className="text-xs sm:text-base text-white/80">UAE Tax Compliant | FTA Approved Format</p>
            </div>
            <div className="badge badge-lg bg-white/20 text-white border-0 p-2 sm:p-4 shrink-0">
              <span className="text-lg sm:text-2xl font-bold">🇦🇪</span>
            </div>
          </div>
        </div>

        {/* Main Form Card */}
        <div className="bg-base-100 rounded-b-2xl shadow-2xl p-4 sm:p-8 mb-6 sm:mb-8 border-t-4 border-primary">
          <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
            {/* PO Details Section */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-4 sm:p-6 rounded-xl">
              <h3 className="text-lg sm:text-xl font-bold text-primary mb-3 sm:mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary rounded-full"></span>
                Purchase Order Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-primary">📄</span> PO NO.
                    </span>
                  </label>
                  <input
                    type="text"
                    className={`input input-bordered border-2 focus:border-primary ${errors.poNumber ? 'input-error' : ''}`}
                    value={formData.poNumber}
                    onChange={(e) => updateField('poNumber', e.target.value)}
                    placeholder="PO-202603-001"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-secondary">📅</span> PO DATE
                    </span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered border-2 focus:border-secondary"
                    value={formData.poDate}
                    onChange={(e) => updateField('poDate', e.target.value)}
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-accent">⏰</span> REQUIRED DATE
                    </span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered border-2 focus:border-accent"
                    value={formData.requiredDate}
                    onChange={(e) => updateField('requiredDate', e.target.value)}
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-info">💰</span> CURRENCY
                    </span>
                  </label>
                  <select
                    className="select select-bordered border-2 focus:border-info"
                    value={formData.currency}
                    onChange={(e) => updateField('currency', e.target.value)}
                  >
                    <option value="AED">AED - UAE Dirham</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">PAYMENT TERMS</span>
                    </label>
                    <select
                      className="select select-bordered border-2"
                      value={formData.paymentTerms}
                      onChange={(e) => updateField('paymentTerms', e.target.value)}
                    >
                      <option>Net 15</option>
                      <option>Net 30</option>
                      <option>Net 45</option>
                      <option>Net 60</option>
                      <option>Due on Receipt</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Supplier Section */}
            <div className="bg-gradient-to-r from-secondary/10 to-accent/10 p-6 rounded-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-secondary flex items-center gap-2">
                  <span className="w-1 h-6 bg-secondary rounded-full"></span>
                  Supplier Details
                </h3>
                <button
                  type="button"
                  className="btn btn-sm btn-outline btn-secondary"
                  onClick={() => setShowSupplierForm(!showSupplierForm)}
                >
                  {showSupplierForm ? '📋 Select Supplier' : '➕ Add New'}
                </button>
              </div>

              {!showSupplierForm && suppliers.length > 0 ? (
                <div className="form-control mb-4">
                  <select
                    className="select select-bordered border-2"
                    onChange={(e) => handleSupplierSelect(e.target.value)}
                    defaultValue=""
                  >
                    <option value="" disabled>Select a supplier</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name} - {supplier.trn || 'No TRN'}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">SUPPLIER NAME *</span>
                  </label>
                  <input
                    type="text"
                    className={`input input-bordered border-2 ${errors.supplierName ? 'input-error' : ''}`}
                    value={formData.supplierName}
                    onChange={(e) => updateField('supplierName', e.target.value)}
                    placeholder="Tech Supplies LLC"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">TRN (TAX REG. NO.)</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 font-mono"
                    value={formData.supplierTrn}
                    onChange={(e) => updateField('supplierTrn', e.target.value)}
                    placeholder="15-digit TRN"
                    maxLength="15"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">EMAIL</span>
                  </label>
                  <input
                    type="email"
                    className="input input-bordered border-2"
                    value={formData.supplierEmail}
                    onChange={(e) => updateField('supplierEmail', e.target.value)}
                    placeholder="orders@supplier.ae"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">ADDRESS</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2"
                    value={formData.supplierAddress}
                    onChange={(e) => updateField('supplierAddress', e.target.value)}
                    placeholder="Industrial Area, Dubai"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">CITY / EMIRATE</span>
                  </label>
                  <select
                    className="select select-bordered border-2"
                    value={formData.supplierCity}
                    onChange={(e) => updateField('supplierCity', e.target.value)}
                  >
                    <option value="">Select...</option>
                    {emirates.map(emirate => (
                      <option key={emirate} value={emirate}>{emirate}</option>
                    ))}
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">COUNTRY</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2"
                    value={formData.supplierCountry}
                    onChange={(e) => updateField('supplierCountry', e.target.value)}
                    placeholder="UAE"
                  />
                </div>
              </div>
            </div>

            {/* Line Items Section */}
            <div className="bg-gradient-to-r from-accent/10 to-primary/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-accent mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-accent rounded-full"></span>
                Line Items
              </h3>

              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                    <tr>
                      <th className="rounded-tl-lg">#</th>
                      <th>DESCRIPTION</th>
                      <th>QTY</th>
                      <th>UNIT</th>
                      <th>UNIT PRICE</th>
                      <th>DISC %</th>
                      <th>VAT %</th>
                      <th className="rounded-tr-lg">TOTAL</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const itemTotal = calculateItemTotal(item);

                      return (
                        <tr key={index} className="hover:bg-base-200 transition-colors">
                          <td className="font-bold text-primary">{index + 1}</td>
                          <td>
                            <input
                              type="text"
                              className="input input-bordered input-sm w-40"
                              value={item.description}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                              placeholder="Description"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input input-bordered input-sm w-16"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                              min="0.01"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <select
                              className="select select-bordered select-sm w-20"
                              value={item.unit}
                              onChange={(e) => updateItem(index, 'unit', e.target.value)}
                            >
                              <option>Unit</option>
                              <option>Hour</option>
                              <option>Day</option>
                              <option>Service</option>
                              <option>Piece</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input input-bordered input-sm w-20"
                              value={item.unitPrice}
                              onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <div className="relative">
                              <input
                                type="number"
                                className="input input-bordered input-sm w-16 pr-6"
                                value={item.discountPercent}
                                onChange={(e) => updateItem(index, 'discountPercent', e.target.value)}
                                min="0"
                                max="100"
                                step="0.1"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs">%</span>
                            </div>
                          </td>
                          <td>
                            <select
                              className="select select-bordered select-sm w-16"
                              value={item.vatPercent}
                              onChange={(e) => updateItem(index, 'vatPercent', e.target.value)}
                            >
                              <option value="0">0%</option>
                              <option value="5">5%</option>
                            </select>
                          </td>
                          <td className="text-right font-mono font-bold text-success">
                            AED {itemTotal.toFixed(2)}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs text-error hover:bg-error/20"
                              onClick={() => removeItem(index)}
                              disabled={items.length === 1}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                className="btn btn-outline btn-accent btn-sm mt-4 hover:scale-105 transition-transform"
                onClick={addItem}
              >
                ➕ Add Line Item
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="stat bg-gradient-to-br from-info/20 to-info/5 rounded-xl">
                <div className="stat-figure text-info">
                  <span className="text-2xl">💰</span>
                </div>
                <div className="stat-title">Subtotal</div>
                <div className="stat-value text-info text-2xl">AED {subtotal.toFixed(2)}</div>
              </div>

              <div className="stat bg-gradient-to-br from-warning/20 to-warning/5 rounded-xl">
                <div className="stat-figure text-warning">
                  <span className="text-2xl">🏷️</span>
                </div>
                <div className="stat-title">Discount</div>
                <div className="stat-value text-warning text-2xl">AED {totalDiscount.toFixed(2)}</div>
              </div>

              <div className="stat bg-gradient-to-br from-secondary/20 to-secondary/5 rounded-xl">
                <div className="stat-figure text-secondary">
                  <span className="text-2xl">🧾</span>
                </div>
                <div className="stat-title">VAT (5%)</div>
                <div className="stat-value text-secondary text-2xl">AED {vatTotal.toFixed(2)}</div>
              </div>

              <div className="stat bg-gradient-to-br from-success/20 to-success/5 rounded-xl">
                <div className="stat-figure text-success">
                  <span className="text-2xl">💵</span>
                </div>
                <div className="stat-title">Total Amount</div>
                <div className="stat-value text-success text-2xl">AED {totalDue.toFixed(2)}</div>
              </div>
            </div>

            {/* File Upload Section */}
            <div className="bg-gradient-to-r from-purple/10 to-pink/10 p-6 rounded-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-purple-600 flex items-center gap-2">
                  <span className="w-1 h-6 bg-purple-600 rounded-full"></span>
                  Purchase Invoice Upload Session
                </h3>
                <div className="flex gap-2">
                  {!uploadSession.isActive ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline btn-purple"
                      onClick={startUploadSession}
                    >
                      🚀 Start Upload Session
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline btn-red"
                      onClick={endUploadSession}
                    >
                      🛑 End Session
                    </button>
                  )}
                </div>
              </div>

              {uploadSession.isActive && (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <div className="text-purple-600 mb-2">
                        <span className="text-4xl">📎</span>
                      </div>
                      <p className="text-lg font-medium">Click to upload purchase invoices</p>
                      <p className="text-sm text-gray-500">PDF, JPG, PNG, DOC, DOCX up to 10MB each</p>
                    </label>
                  </div>

                  {uploadedFiles.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-purple-700">Uploaded Files ({uploadedFiles.length})</h4>
                      {uploadedFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between bg-purple-50 p-3 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">📄</span>
                            <div>
                              <p className="font-medium text-sm">{file.name}</p>
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-xs btn-ghost text-red-500"
                            onClick={() => removeFile(file.id)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Company Info and Compliance */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6\">
              {/* Company Section */}
              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 p-4 sm:p-6 rounded-xl\">
                {/* <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                  <span className="w-1 h-6 bg-primary rounded-full"></span>
                  Your Company
                </h3> */}
                <div className="space-y-4">
                  {company.logo && (
                    <img src={company.logo} alt="Company Logo" className="h-12 object-contain" />
                  )}
                  <div>
                    <p className="font-bold text-lg">{company.name}</p>
                    <p className="text-sm text-primary">TRN: {company.trn}</p>
                    <p className="text-sm text-base-600 mt-2">{company.address}</p>
                  </div>
                  <div className="badge badge-outline badge-primary p-3">via API.getCompany()</div>
                </div>
              </div>

              {/* Compliance Checklist */}
              <div className="bg-gradient-to-br from-success/10 to-accent/10 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-success mb-4 flex items-center gap-2">
                  <span className="w-1 h-6 bg-success rounded-full"></span>
                  PO Compliance Checklist
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-2 bg-base-100 rounded-lg">
                    <input type="checkbox" className="checkbox checkbox-success" checked readOnly />
                    <span className="flex-1">Supplier TRN shown</span>
                    <span className="badge badge-success badge-sm">✓</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-base-100 rounded-lg">
                    <input type="checkbox" className="checkbox checkbox-success" checked readOnly />
                    <span className="flex-1">VAT shown separately</span>
                    <span className="badge badge-success badge-sm">✓</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-base-100 rounded-lg">
                    <input type="checkbox" className="checkbox checkbox-success" checked readOnly />
                    <span className="flex-1">Taxable amount shown</span>
                    <span className="badge badge-success badge-sm">✓</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-base-100 rounded-lg">
                    <input type="checkbox" className="checkbox checkbox-warning"
                      checked={!!formData.supplierTrn} readOnly />
                    <span className="flex-1">Supplier TRN (optional)</span>
                    <span className={`badge ${formData.supplierTrn ? 'badge-success' : 'badge-ghost'} badge-sm`}>
                      {formData.supplierTrn ? '✓' : '○'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-base-100 rounded-lg">
                    <input type="checkbox" className="checkbox checkbox-success"
                      checked={!!formData.supplierName} readOnly />
                    <span className="flex-1">Supplier name filled</span>
                    <span className="badge badge-success badge-sm">✓</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes & Payment Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-gradient-to-br from-info/10 to-info/5 p-4 sm:p-6 rounded-xl">
                <label className="label">
                  <span className="label-text font-bold text-info text-base sm:text-lg flex items-center gap-2">
                    <span className="w-1 h-5 sm:h-6 bg-info rounded-full"></span>
                    NOTES / TERMS
                  </span>
                </label>
                <textarea
                  className="textarea textarea-bordered textarea-sm sm:textarea-md h-24 sm:h-32 w-full border-2 focus:border-info"
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  placeholder="Please deliver to our main office. Contact person: John Doe - 050-1234567"
                ></textarea>
              </div>

              <div className="bg-gradient-to-br from-warning/10 to-warning/5 p-4 sm:p-6 rounded-xl">
                <label className="label">
                  <span className="label-text font-bold text-warning text-base sm:text-lg flex items-center gap-2">
                    <span className="w-1 h-5 sm:h-6 bg-warning rounded-full"></span>
                    PAYMENT DETAILS
                  </span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    className="input input-bordered input-sm w-full border-2 focus:border-warning"
                    value={formData.bankDetails.iban}
                    onChange={(e) => updateBankDetails('iban', e.target.value)}
                    placeholder="IBAN: AE07 0331 2345 6789 0123 456"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      className="input input-bordered input-sm border-2 focus:border-warning"
                      value={formData.bankDetails.bankName}
                      onChange={(e) => updateBankDetails('bankName', e.target.value)}
                      placeholder="Bank: Emirates NBD"
                    />
                    <input
                      type="text"
                      className="input input-bordered input-sm border-2 focus:border-warning"
                      value={formData.bankDetails.swift}
                      onChange={(e) => updateBankDetails('swift', e.target.value)}
                      placeholder="Swift: EBILAEAE"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-4 sm:pt-6 border-t\">\n              <button
                type="button"
                className="btn btn-ghost btn-sm sm:btn-lg hover:bg-base-200 order-2 sm:order-1\"
                onClick={() => window.history.back()}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-lg bg-gradient-to-r from-primary to-secondary border-0 hover:scale-105 transition-transform shadow-lg"
              >
                💾 Save Purchase Order
              </button>
            </div>
          </form>
        </div>

        {/* Hidden PDF Download Link */}
        {pdfData && (
          <div style={{ display: 'none' }}>
            <PDFDownloadLink
              document={<PurchaseOrderPDFDocument purchaseOrderData={pdfData} />}
              fileName={`PurchaseOrder-${pdfData.poNumber || 'New'}.pdf`}
            >
              {({ loading }) => !loading && 'Download PDF'}
            </PDFDownloadLink>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-base-500 mt-4">
          <p>🇦🇪 UAE VAT Compliant | FTA Approved Format | All amounts in {formData.currency}</p>
        </div>
      </div>

      <style jsx>{`

      `}</style>
    </div>
  );
};

export default PurchaseOrderEntry;